import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { PropsWithChildren, ReactNode, useEffect, useRef } from 'react';

import { ModalCore } from 'src/shared/components/core/modal-core/modal-core';
import { checkIsNode } from 'src/shared/utils/check-is-node';

import styles from './modal.module.scss';

type Props = PropsWithChildren<{
  width?: number;
  maxWidth?: number;
  minWidth?: number;
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
  isOpened: boolean;
  closeOnClickOutside?: boolean;
  title?: string | ReactNode;
  showCloseCross?: boolean;
  handleClose?: () => void;
  className?: string;
}>;

export const Modal = observer(function Modal({
  maxWidth,
  width,
  minWidth,
  top = 64,
  right,
  bottom,
  left,
  isOpened,
  closeOnClickOutside = true,
  title,
  showCloseCross = true,
  children,
  handleClose,
  className,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!closeOnClickOutside || !isOpened || !handleClose) return;

    const current = containerRef.current!;

    const handleClick = (event: MouseEvent) => {
      if (!checkIsNode(event.target)) return;
      if (!current.contains(event.target)) handleClose();
    };

    document.body.addEventListener('pointerdown', handleClick);

    return () => document.body.removeEventListener('pointerdown', handleClick);
  }, [isOpened, closeOnClickOutside, handleClose]);

  return (
    <ModalCore isOpened={isOpened}>
      <div
        ref={containerRef}
        className={clsx(className, styles.wrapper)}
        style={{ width, maxWidth, minWidth, top, right, bottom, left }}
      >
        {!!title && typeof title === 'string' ? <h2 className={styles.title}>{title}</h2> : title}
        {!!handleClose && showCloseCross && <button className={styles.closeButton} onClick={handleClose} />}
        {children}
      </div>
    </ModalCore>
  );
});
